"use client";

import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/components/auth/AuthProvider";
import { useToolAccess } from "@/lib/home/planPreview";
import { useServiceSettings, type ServiceSettingsRow } from "@/hooks/useSiteSettings";
import { hasFeature } from "@/lib/billing/planLabel";
import { resolveToolSortOrder } from "@/lib/toolDisplayOrder";
import { resolveToolPreviewImage } from "@/lib/toolPreviewImages";
import { getRecentTools } from "@/lib/recent-tools";
import { getFavoriteTools } from "@/lib/favorite-tools";
import { SUBSCRIBER_HUB_TOOLS, type HubTool } from "./hubData";
import { HubToolCard } from "./HubToolCard";
import { HubAssetDiscovery } from "./HubAssetDiscovery";

const MAX_ROW_TOOLS = 4;

export function HubOverlay({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { permissions, isAdmin } = useToolAccess();
  const { data: serviceSettings = [] } = useServiceSettings();
  const [recentHrefs, setRecentHrefs] = useState<string[]>([]);
  const [favoriteHrefs, setFavoriteHrefs] = useState<string[]>([]);
  const toolOrderByHref = useMemo(() => {
    const tools = SUBSCRIBER_HUB_TOOLS;
    return new Map(tools.map((tool, index) => [tool.href, index]));
  }, []);
  const sortToolsByAdminOrder = useMemo(
    () => (tools: HubTool[]) =>
      [...tools].sort((a, b) => {
        const aOrder = resolveToolSortOrder({ href: a.href, featureKey: a.featureKey, serviceSettings });
        const bOrder = resolveToolSortOrder({ href: b.href, featureKey: b.featureKey, serviceSettings });
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (toolOrderByHref.get(a.href) ?? Number.MAX_SAFE_INTEGER) - (toolOrderByHref.get(b.href) ?? Number.MAX_SAFE_INTEGER);
      }),
    [serviceSettings, toolOrderByHref],
  );

  const toolByHref = useMemo(() => {
    const map = new Map<string, HubTool>();
    for (const tool of SUBSCRIBER_HUB_TOOLS) map.set(tool.href, tool);
    return map;
  }, []);

  // Refresh recents/favorites every time the overlay opens AND whenever favorites
  // change while it's open (e.g. user toggles a star inside the popup).
  useEffect(() => {
    if (!open) return;
    setRecentHrefs(getRecentTools());
    setFavoriteHrefs(getFavoriteTools());
    const onChange = () => setFavoriteHrefs(getFavoriteTools());
    window.addEventListener("workshop-favorites-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("workshop-favorites-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, [open]);

  useEffect(() => {
    const closeOverlay = () => onOpenChange(false);
    window.addEventListener("workshop:close-global-popups", closeOverlay);
    return () => {
      window.removeEventListener("workshop:close-global-popups", closeOverlay);
    };
  }, [onOpenChange]);

  const recentTools = useMemo(
    () =>
      sortToolsByAdminOrder(
        recentHrefs
        .map((h) => toolByHref.get(h))
        .filter((t): t is HubTool => Boolean(t))
      ).slice(0, MAX_ROW_TOOLS),
    [recentHrefs, sortToolsByAdminOrder, toolByHref],
  );
  const favoriteTools = useMemo(
    () =>
      sortToolsByAdminOrder(
        favoriteHrefs
        .map((h) => toolByHref.get(h))
        .filter((t): t is HubTool => Boolean(t))
      ).slice(0, MAX_ROW_TOOLS),
    [favoriteHrefs, sortToolsByAdminOrder, toolByHref],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto border-black/60 p-0 shadow-[0_30px_90px_rgba(0,0,0,0.55)]"
        style={{ width: "min(92vw, 72rem)", maxWidth: "min(92vw, 72rem)" }}
        aria-describedby={undefined}
      >
        <DialogHeader className="border-b border-black/40 px-6 py-4">
          <DialogTitle>Hub</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 px-6 py-5">
          {favoriteTools.length > 0 && (
            <ToolRow
              label="Starred"
              hint="Your pinned tools"
              tools={favoriteTools}
              isAdmin={isAdmin}
              permissions={permissions}
              serviceSettings={serviceSettings}
            />
          )}
          {recentTools.length > 0 && (
            <ToolRow
              label="Recent"
              hint="Tools you opened lately"
              tools={recentTools}
              isAdmin={isAdmin}
              permissions={permissions}
              serviceSettings={serviceSettings}
            />
          )}
          <HubAssetDiscovery />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ToolRow({
  label,
  hint,
  tools,
  isAdmin,
  permissions,
  serviceSettings,
}: {
  label: string;
  hint: string;
  tools: HubTool[];
  isAdmin: boolean;
  permissions: ReturnType<typeof useAuth>["permissions"];
  serviceSettings: ServiceSettingsRow[];
}) {
  return (
    <section>
      <div className="flex items-baseline justify-between gap-3 border-b border-white/10 pb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-foreground/85">
          {label}
        </span>
        <span className="truncate text-[11px] text-muted-foreground/80">{hint}</span>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {tools.map((tool) => (
          <HubToolCard
            key={tool.href}
            tool={tool}
            locked={!hasFeature(permissions, tool.featureKey, { isAdmin })}
            imageUrl={resolveToolPreviewImage({
              href: tool.href,
              featureKey: tool.featureKey,
              serviceSettings,
            })}
          />
        ))}
      </div>
    </section>
  );
}
